import { useState, useRef, useEffect, useCallback } from "react"
import { message } from "antd"

interface UseSpeechRecognitionProps {
    isMicOn: boolean
    connected: boolean
    isMyraSpeaking: boolean
    stream: MediaStream | null
    audioTrackRef: React.MutableRefObject<MediaStreamTrack | null>
    sendMessage: (content: string) => void
    muteMicInRecording: () => void
    unmuteMicInRecording: () => void
}

export const useSpeechRecognition = ({
    isMicOn,
    connected,
    isMyraSpeaking,
    stream,
    audioTrackRef,
    sendMessage,
    muteMicInRecording,
    unmuteMicInRecording,
}: UseSpeechRecognitionProps) => {
    const [isListening, setIsListening] = useState(false)
    const recognitionRef = useRef<any>(null)
    const isBlockedFromSpeakingRef = useRef<boolean>(false)
    const blockTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null)

    // Initialize speech recognition
    useEffect(() => {
        if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
            return
        }

        const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition
        const recognition = new SpeechRecognition()

        recognition.continuous = true
        recognition.interimResults = true
        recognition.lang = "en-US"
        recognition.maxAlternatives = 1

        let lastSentTime = 0
        const MIN_CONFIDENCE = 0.65

        recognition.onstart = () => {
            setIsListening(true)
        }

        recognition.onresult = async (event: any) => {
            if (isMyraSpeaking) {
                return
            }

            if (isBlockedFromSpeakingRef.current) {
                return
            }

            if (audioTrackRef.current && !audioTrackRef.current.enabled) {
                return
            }

            const timeSinceLastMessage = Date.now() - lastSentTime
            if (timeSinceLastMessage < 1000) {
                return
            }

            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i]
                const transcript = result[0].transcript.trim()
                const confidence = result[0].confidence
                const isFinal = result.isFinal

                if (isFinal && transcript.length > 3 && confidence >= MIN_CONFIDENCE) {
                    lastSentTime = Date.now()
                    sendMessage(transcript)
                } else if (isFinal && confidence < MIN_CONFIDENCE) {
                    console.log("🚫 Ignoring low confidence speech:", transcript, "Confidence:", confidence)
                }
            }
        }

        recognition.onerror = (event: any) => {
            if (event.error === "no-speech" || event.error === "aborted") return

            if (event.error === "audio-capture" || event.error === "not-allowed") {
                setIsListening(false)
                if (event.error === "not-allowed") {
                    message.error("Microphone permission denied.")
                }
                return
            }

            console.error("Speech recognition error:", event.error)
            setIsListening(false)

            if (event.error === "network") {
                setTimeout(() => {
                    if (isMicOn && connected && !isMyraSpeaking && !isBlockedFromSpeakingRef.current) {
                        try {
                            recognition.start()
                            setIsListening(true)
                        } catch (error) { }
                    }
                }, 10000)
            }
        }

        recognition.onend = () => {
            setIsListening(false)

            if (
                isMicOn &&
                connected &&
                !isMyraSpeaking &&
                !isBlockedFromSpeakingRef.current &&
                audioTrackRef.current?.enabled
            ) {
                setTimeout(() => {
                    try {
                        recognitionRef.current.start()
                        setIsListening(true)
                    } catch (error) {
                        setIsListening(false)
                    }
                }, 500)
            }
        }

        recognitionRef.current = recognition

        if (isMicOn && connected && !isBlockedFromSpeakingRef.current) {
            try {
                recognition.start()
                setIsListening(true)
            } catch (error) { }
        }

        return () => {
            if (recognitionRef.current) {
                try {
                    recognitionRef.current.stop()
                    setIsListening(false)
                } catch (error) { }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected, isMicOn])

    // Handle Myra speaking - block user input
    useEffect(() => {
        if (!recognitionRef.current) return

        const recognition = recognitionRef.current

        if (isMyraSpeaking) {

            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current)
                cooldownTimerRef.current = null
            }
            if (blockTimeoutRef.current) {
                clearTimeout(blockTimeoutRef.current)
                blockTimeoutRef.current = null
            }

            isBlockedFromSpeakingRef.current = true

            if (isListening) {
                try {
                    recognition.stop()
                    setIsListening(false)
                } catch (error) {
                    console.error("Error stopping recognition:", error)
                }
            }

            if (stream && audioTrackRef.current) {
                audioTrackRef.current.enabled = false
            }

            muteMicInRecording()
        } else {

            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current)
            }

            cooldownTimerRef.current = setTimeout(() => {

                if (stream && audioTrackRef.current && isMicOn) {
                    audioTrackRef.current.enabled = true
                }

                unmuteMicInRecording()

                if (isMicOn && connected && !isListening) {
                    setTimeout(() => {
                        try {
                            recognitionRef.current.start()
                            setIsListening(true)
                        } catch (error) {
                            console.error("Error restarting recognition:", error)
                        }
                    }, 100)
                }

                if (blockTimeoutRef.current) {
                    clearTimeout(blockTimeoutRef.current)
                }
                blockTimeoutRef.current = setTimeout(() => {
                    isBlockedFromSpeakingRef.current = false
                }, 100)
            }, 200)
        }

        return () => {
            if (cooldownTimerRef.current) {
                clearTimeout(cooldownTimerRef.current)
            }
            if (blockTimeoutRef.current) {
                clearTimeout(blockTimeoutRef.current)
            }
        }
    }, [isMyraSpeaking, isMicOn, connected, isListening, stream, audioTrackRef, muteMicInRecording, unmuteMicInRecording])

    // Restart recognition when mic is turned on
    useEffect(() => {
        if (!recognitionRef.current) return

        if (isMicOn && connected && !isMyraSpeaking && !isBlockedFromSpeakingRef.current) {
            if (!isListening) {
                try {
                    recognitionRef.current.start()
                    setIsListening(true)
                } catch (error) { }
            }
        } else if (!isMicOn && isListening) {
            try {
                recognitionRef.current.stop()
                setIsListening(false)
            } catch (error) { }
        }
    }, [isMicOn, isListening, connected, isMyraSpeaking])

    const stopRecognition = useCallback(() => {
        try {
            if (recognitionRef.current) {
                recognitionRef.current.onresult = null
                recognitionRef.current.onend = null
                recognitionRef.current.onerror = null
                recognitionRef.current.stop()
                recognitionRef.current = null
            }
        } catch (error) {
            console.error("Error stopping recognition:", error)
        }
        setIsListening(false)

        if (cooldownTimerRef.current) {
            clearTimeout(cooldownTimerRef.current)
            cooldownTimerRef.current = null
        }

        if (blockTimeoutRef.current) {
            clearTimeout(blockTimeoutRef.current)
            blockTimeoutRef.current = null
        }
    }, [])

    return {
        isListening,
        stopRecognition,
        isBlockedFromSpeakingRef,
    }
}

